import { useContext, useEffect, useLayoutEffect, useRef } from "react";
import { CrmScrollContainerContext } from "../CrmScrollContext";
import { loadCrmScrollOffset, saveCrmScrollOffset } from "../utils/crmViewState";

/**
 * Keeps a CRM list page's scroll position across navigation. The offset is restored
 * once, on the first render where the list content is actually on screen — restoring
 * earlier would clamp to a container that is still showing a spinner.
 *
 * @param storageKey sessionStorage key for this list (see crmViewState key builders)
 * @param isContentReady true once the rows for the current page have rendered; while
 *   false the position is neither restored nor overwritten, so a loading state that
 *   collapses the list doesn't clobber the saved offset with 0.
 */
export const useCrmScrollRestoration = (
  storageKey: string,
  isContentReady: boolean
): void => {
  const container = useContext(CrmScrollContainerContext);
  // The key the offset has already been applied for. Restoring again on every render
  // would yank a user who has since scrolled; a *new* key (e.g. the contacts list
  // switching from Coaches to Players) is a different place and gets its own restore.
  const restoredKeyRef = useRef<string | null>(null);
  const isContentReadyRef = useRef(isContentReady);
  isContentReadyRef.current = isContentReady;

  useLayoutEffect(() => {
    if (!container || restoredKeyRef.current === storageKey || !isContentReady) return;

    restoredKeyRef.current = storageKey;
    const offset = loadCrmScrollOffset(storageKey);
    if (offset > 0) container.scrollTop = offset;
  }, [container, isContentReady, storageKey]);

  useEffect(() => {
    if (!container) return;

    let frame = 0;
    const persist = () => {
      // Only record a position that reflects rendered content.
      if (!isContentReadyRef.current) return;
      saveCrmScrollOffset(storageKey, container.scrollTop);
    };
    const handleScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        persist();
      });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (frame) cancelAnimationFrame(frame);
      // Flush the final position — the throttled save may still be pending when the
      // user clicks through to a detail view.
      persist();
    };
  }, [container, storageKey]);
};
